import type { NavItems } from '@ksyos/design-system';
import { ManagerAuthorization } from '@common/enums';
import type { IEpisodeCategory } from '../../common/interfaces/episodeCategory';
import { fetchJson } from '../store/fetch';
import { getSelectedLanguage } from '../translate';
import { hasOneAuthorization } from './hasAuthorization';

export async function getCarePathMenuItems({
    organizationId,
    groupId,
    employmentId,
    pathname,
}: {
    employmentId?: string;
    groupId?: string;
    organizationId?: string;
    pathname: string;
}): Promise<NavItems[]> {
    const episodeCategories = await getEpisodeCategories({ organizationId, groupId, employmentId });
    const showInactiveEpisodeCategories = hasOneAuthorization(ManagerAuthorization.organizationRead)
        ? await hasInactiveEpisodeCategories(organizationId, groupId)
        : false;

    return getCarePathMenuItemLinks(episodeCategories, showInactiveEpisodeCategories, pathname);
}

export function getCarePathMenuItemsWithoutFetch(
    episodeCategories: IEpisodeCategory[],
    pathname: string,
    showInactiveEpisodeCategories: boolean
): NavItems[] {
    return getCarePathMenuItemLinks(episodeCategories, showInactiveEpisodeCategories, pathname);
}

export async function getEpisodeCategories({
    organizationId,
    groupId,
    employmentId,
}: {
    employmentId?: string;
    groupId?: string;
    organizationId?: string;
}) {
    const group = groupId ? `groupId=${groupId}&includeServiceGroups=true&isActive=${true}` : '';
    const organization = organizationId ? `&organizationId=${organizationId}` : '';
    const employment = employmentId ? `&employmentId=${employmentId}` : '';
    const url = organizationId
        ? `/api/episodeCategories?isActive=true&isConsumer=true&isProvider=true&isReader=true&isSupervisor=true${organization}${employment}&excludeServicesWithoutServiceGroup=true&isServiceEmployment=true`
        : '/api/serviceGroupSettings?' + group;

    const result = await fetchJson(url, 'GET');
    const groupEpisodeCategoriesResults = groupId
        ? result.json.items
              .map((items: any) => {
                  return items.service.episodeCategory as IEpisodeCategory[];
              })
              .filter(
                  (value: any, index: number, self: any) => self.map((x: any) => x.name).indexOf(value.name) === index
              )
        : [];
    const activeGroupEpisodeCategories = [
        ...groupEpisodeCategoriesResults.filter((episodeCategories: IEpisodeCategory) => episodeCategories.isActive),
    ];

    return organizationId ? (result.json.items as IEpisodeCategory[]) : activeGroupEpisodeCategories;
}

export async function hasInactiveEpisodeCategories(organizationId?: string, groupId?: string) {
    const group = groupId ? `&groupId=${groupId}&includeServiceGroups=true` : '';
    const url = organizationId
        ? `/api/organizations/${organizationId}/inactiveEpisodeCategories`
        : '/api/serviceGroupSettings?' + group;
    const result = await fetchJson(url, 'GET');
    const groupEpisodeCategoriesResults = groupId
        ? result.json.items
              .map((items: any) => {
                  return items.service.episodeCategory as IEpisodeCategory[];
              })
              .filter(
                  (value: any, index: number, self: any) => self.map((x: any) => x.name).indexOf(value.name) === index
              )
        : [];
    const inactiveGroupEpisodeCategories = [
        ...groupEpisodeCategoriesResults.filter((episodeCategories: IEpisodeCategory) => !episodeCategories.isActive),
    ];

    return organizationId ? result.json.items.length > 0 : inactiveGroupEpisodeCategories.length > 0;
}

function getCarePathMenuItemLinks(
    episodeCategories: IEpisodeCategory[],
    showInactiveEpisodeCategories: boolean,
    pathname: string
) {
    const translate = getSelectedLanguage();

    const carePathSubItems: NavItems[] = episodeCategories
        .map((category) => {
            return {
                key: `carePath-${category.name}`,
                label: translate.episodeCategoryEnum[category.name],
                to: `${pathname}/carePaths/${category.name}`,
                variant: 'link' as const,
            };
        })
        .sort((a, b) => {
            return a.label < b.label ? -1 : 1;
        });

    if (showInactiveEpisodeCategories) {
        carePathSubItems.push({
            key: 'inactiveCarePaths',
            label: translate.episodeCategory.inactiveEpisodeCategories,
            to: `${pathname}/inactiveCarePaths`,
            variant: 'link' as const,
        });
    }

    return [
        {
            items: carePathSubItems,
            key: 'carePath',
            label: translate.episodeCategory.episodeCategories,
            to: `${pathname}/carePaths`,
            variant: 'link' as const,
        },
    ];
}
